import { Link } from "react-router-dom";
import {
  UploadSimple, MagnifyingGlass, Cube, ChartLineUp, FileText, ArrowRight,
  Users, FunnelSimple, Graph, Certificate,
} from "@phosphor-icons/react";
import { Section, SectionHeading, FeatureCard, CalloutCard, BulletList } from "@/components/marketing/MarketingUI";
import CockpitHero from "@/components/marketing/CockpitHero";
import useSEO from "@/lib/useSEO";

const HIW_COCKPIT = {
  label: "SRES · The Five-Step Loop",
  kpis: [
    { label: "STEPS",       to: 5,  suffix: "",     delta: "One afternoon",    deltaColor: "text-slate-500" },
    { label: "ARTEFACTS",   to: 12, suffix: "+",    delta: "PDF · DOCX · JSON", deltaColor: "text-slate-500" },
    { label: "REPRODUCIBLE", to: 100, suffix: "%",  delta: "Every run",         deltaColor: "text-brand-blue" },
  ],
  variogram: { r2: "0.95", range: "175 m", sill: "0.71", nugget: "0.07" },
  classification: [
    { label: "Measured",  pct: 43 },
    { label: "Indicated", pct: 34 },
    { label: "Inferred",  pct: 23 },
  ],
  status: {
    left: "OK · 3.1 s per 100k blocks",
    right: "5 steps · 1 afternoon",
  },
};

const STEPS = [
  {
    icon: UploadSimple,
    step: "01",
    title: "Import your drillhole database",
    body: "Upload Collar, Survey, Assay, Geology and (optionally) CRM / Blank / Duplicate CSVs. SRES auto-detects industry column aliases (BHID → hole_id, easting → x, from/to intervals, etc.) so most files map on the first try.",
    outputs: [
      "Validated tables with row counts and coverage stats.",
      "Per-hole trace preview and lithology roll-up.",
      "Auto-suggested EPSG, density defaults and cut-off ranges.",
    ],
  },
  {
    icon: MagnifyingGlass,
    step: "02",
    title: "Validate, QAQC and cleanse",
    body: "Deep validation catches missing surveys, out-of-hole assays, geometry overlaps and negative depths before they contaminate a block model. QAQC analytics deliver control-chart envelopes on CRMs, blanks and duplicates.",
    outputs: [
      "Categorised issue log (error / warning / info) with affected hole IDs.",
      "Precision, bias, HARD, top-cut recommendation and log-probability plots.",
      "Data validation and QAQC PDF and JSON exports.",
    ],
  },
  {
    icon: Cube,
    step: "03",
    title: "Build the block model",
    body: "Pick an interpolator — IDW, Nearest Neighbour or Ordinary Kriging — and let SRES compute a domained block model with composite lengths, variogram fitting and per-lithology density.",
    outputs: [
      "3D block model with tonnage, grade and classification per block.",
      "Grade-tonnage curves with a commodity-aware cut-off range.",
      "Multiple 3D views: by grade, by lithology, ore only, waste only.",
    ],
  },
  {
    icon: ChartLineUp,
    step: "04",
    title: "Optimise the pit and stress-test economics",
    body: "Run the floating-cone pit optimiser with your own price and cost assumptions. Sweep cut-off vs NPV, run price / cost / recovery sensitivity, and simulate Monte-Carlo NPV distributions.",
    outputs: [
      "Pit shell with ore, waste, strip ratio and NPV summary.",
      "Cutoff-sensitivity, sensitivity report PDF and DOCX.",
      "P10 / P50 / P90 Monte-Carlo simulation with histogram.",
    ],
  },
  {
    icon: FileText,
    step: "05",
    title: "Generate the Competent Person Report",
    body: "Assemble a full CPR PDF (or DOCX) with cover, methodology, findings, sensitivity and appendices — with signature blocks and watermarked public share links.",
    outputs: [
      "JORC / NI 43-101 / SAMREC-aligned narrative and figures.",
      "Public share URLs with expiry, view tracking and audit logs.",
      "Per-borehole continuous logs bundled as a companion PDF.",
    ],
  },
];

export default function HowItWorksPage() {
  useSEO({
    title: "How it works · From drillhole to signed CPR",
    description: "The five-step SRES loop — Import, Validate & QAQC, Estimate, Optimise, Report — with concrete outputs at every stage.",
    keywords: "SRES how it works, drillhole ingest, QAQC, kriging, CPR report",
    path: "/how-it-works",
  });
  return (
    <div data-testid="how-it-works-page">
      <CockpitHero
        eyebrow="How it works"
        title={<>From raw drillhole database to a <span className="text-brand-blue">signed resource report</span> — in one afternoon.</>}
        description="SRES is opinionated on purpose. Every step in the workflow is a first-class module that hands its outputs to the next, so nothing gets lost in translation between tools."
        primaryCta={{ label: "Book a session", to: "/contact" }}
        secondaryCta={{ label: "See the modules", to: "/product" }}
        cockpit={HIW_COCKPIT}
        testid="hiw-hero"
      />

      <Section className="py-20 md:py-28" testid="hiw-steps">
        <div className="max-w-4xl">
          <SectionHeading
            eyebrow="The five-step loop"
            title="A defensible resource workflow, end to end."
            description="Each step below can be executed in under an hour on a real project — even faster once the team has run through the loop once."
          />
        </div>

        <div className="mt-20 space-y-16">
          {STEPS.map((s, i) => (
            <div
              key={s.step}
              className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-8 lg:gap-14"
              data-testid={`hiw-step-${s.step}`}
            >
              <div className="flex lg:flex-col items-center lg:items-start gap-5">
                <div className="w-14 h-14 rounded-xl border border-slate-200 flex items-center justify-center shrink-0">
                  <s.icon size={22} weight="regular" className="text-slate-700" />
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.32em] text-slate-400 font-mono">Step</div>
                  <div className="mt-1 font-heading font-black text-[44px] text-slate-900 tracking-[-0.02em] leading-none">{s.step}</div>
                </div>
              </div>
              <div className="lg:border-l lg:border-slate-200 lg:pl-10">
                <div className="font-heading font-bold text-slate-900 text-[24px] tracking-tight leading-tight">{s.title}</div>
                <p className="mt-4 text-slate-600 text-[15.5px] leading-[1.7] max-w-3xl">{s.body}</p>
                <div className="mt-8 inline-flex items-center gap-2.5 text-[10.5px] font-semibold uppercase tracking-[0.28em] text-slate-500 mb-3">
                  <span className="w-6 h-px bg-brand-blue" />
                  Outputs delivered
                </div>
                <BulletList items={s.outputs} testid={`hiw-step-${s.step}-outputs`} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28 bg-[#F7F8FB]" testid="hiw-personas">
        <SectionHeading
          eyebrow="Built for real teams"
          title="Who benefits — and how."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <FeatureCard
            icon={Users}
            title="Resource geologists"
            description="Skip the CSV munging and legacy license servers — spend the day on domaining, variography and defensible classification."
            testid="hiw-persona-geologist"
          />
          <FeatureCard
            icon={Graph}
            title="Mine planners"
            description="Get pit shells, NPV, and sensitivity charts against your own commodity prices — with a live cut-off slider."
            testid="hiw-persona-planner"
          />
          <FeatureCard
            icon={Certificate}
            title="Competent Persons"
            description="Assemble sign-off-ready CPR PDFs with narrative, figures and watermarked share links in a few clicks."
            testid="hiw-persona-cp"
          />
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="hiw-cta">
        <CalloutCard
          eyebrow="Ready to see it live?"
          title="We will run the entire five-step loop on your data during a 45-minute session."
          description="No slideware. No pre-canned demos. Just your own drillhole database, on the real product."
          cta="Book a session"
          to="/contact"
          testid="hiw-cta-callout"
        />
      </Section>
    </div>
  );
}
